import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import {
  listTirages, listTirageClosures, createTirageClosure, deleteTirageClosure,
} from '../../api/tirages';
import { todayISO } from '../../utils/date';
import './CloseSalesPage.css';

// weekday : convention date.weekday() cote backend -> 0 = lundi ... 6 = dimanche.
const WEEKDAYS = [
  { v: 0, label: 'Lun' }, { v: 1, label: 'Mar' }, { v: 2, label: 'Mer' },
  { v: 3, label: 'Jeu' }, { v: 4, label: 'Ven' }, { v: 5, label: 'Sam' }, { v: 6, label: 'Dim' },
];
const weekdayLabel = (v) => (WEEKDAYS.find((d) => d.v === v)?.label ?? `J${v}`);

const CloseSalesPage = () => {
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('0');
  const [lotteryName, setLotteryName] = useState('TOUS');
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);

  const [tirages, setTirages] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return Promise.all([listTirages(), listTirageClosures()])
      .then(([tira, clos]) => { setTirages(tira); setClosures(clos); })
      .catch(() => setError('Impossible de charger les tirages / fermetures — verifie que le serveur backend tourne.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = (v) => {
    setMessage('');
    setSelectedWeekdays((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  // Loterie "0" = toutes les loteries du catalogue, sinon celle dont le code correspond.
  const targetCodes = () => {
    if (String(lottery) === '0') return tirages.map((t) => t.code);
    const code = Number(lottery);
    return tirages.some((t) => t.code === code) ? [code] : [];
  };

  const runClosures = async (buildPayloads, okMessage) => {
    const codes = targetCodes();
    if (codes.length === 0) { setError('Choisis une loterie valide.'); return; }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      for (const code of codes) {
        for (const payload of buildPayloads(code)) {
          await createTirageClosure(payload);
        }
      }
      await load();
      setMessage(okMessage);
    } catch (err) {
      setError('Enregistrement de la fermeture impossible.');
    } finally {
      setBusy(false);
    }
  };

  const handleCloseWeekdays = () => {
    if (selectedWeekdays.length === 0) { setError('Coche au moins un jour de la semaine.'); return; }
    runClosures(
      (code) => selectedWeekdays.map((weekday) => ({ tirage: code, weekday })),
      'Fermeture(s) recurrente(s) enregistree(s).',
    );
  };

  const handleCloseDate = () => {
    if (!date) { setError('Choisis une date.'); return; }
    runClosures(
      (code) => [{ tirage: code, date }],
      `Fermeture(s) enregistree(s) pour le ${date}.`,
    );
  };

  const handleReopen = async (id) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await deleteTirageClosure(id);
      await load();
    } catch (err) {
      setError('Reouverture impossible.');
    } finally {
      setBusy(false);
    }
  };

  // Heure de fermeture (end_time) par code tirage, depuis le catalogue.
  const endTimeByCode = Object.fromEntries(tirages.map((t) => [t.code, t.end_time]));
  const fmtEndTime = (v) => (v ? String(v).slice(0, 5) : '—'); // "22:00:00" -> "22:00"

  // Regroupe les fermetures par tirage pour le tableau.
  const byTirage = [];
  const idx = {};
  closures.forEach((c) => {
    if (!(c.tirage in idx)) {
      idx[c.tirage] = byTirage.length;
      byTirage.push({
        code: c.tirage, name: c.tirage_name, endTime: endTimeByCode[c.tirage],
        weekdays: [], dates: [],
      });
    }
    const row = byTirage[idx[c.tirage]];
    if (c.weekday !== null && c.weekday !== undefined) row.weekdays.push({ id: c.id, weekday: c.weekday });
    else row.dates.push({ id: c.id, date: c.date });
  });
  byTirage.sort((a, b) => a.code - b.code);
  byTirage.forEach((r) => r.weekdays.sort((a, b) => a.weekday - b.weekday));

  return (
    <Layout>
      <p className="cs-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/central" className="bc-link">Central</Link>
        {' >> '}
        <span className="bc-current">Close Sales</span>
      </p>

      <div className="cs-card">
        <div className="cs-card-header">
          <span className="cs-card-title">Fermer une loterie</span>
        </div>
        <div className="cs-card-body">
          <p className="cs-help">
            Une loterie fermee ne peut plus recevoir de fiche des banques de la company,
            les jours ou la fermeture s'applique.
          </p>

          <div className="cs-row">
            <label className="cs-label">Lottery</label>
            <LotteryCodeField
              value={lottery}
              name={lotteryName}
              onChange={(code) => { setLottery(code); setLotteryName(''); }}
              onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
              inputClass="cs-input cs-input-sm"
              iconClass="cs-icon-btn"
              nameClass="cs-lottery-name"
            />
            {String(lottery) !== '0' && endTimeByCode[Number(lottery)] && (
              <span className="cs-lottery-end">fin {fmtEndTime(endTimeByCode[Number(lottery)])}</span>
            )}
          </div>

          <div className="cs-row cs-row-top">
            <label className="cs-label">Jours fermes</label>
            <div className="cs-weekdays">
              {WEEKDAYS.map((d) => (
                <label key={d.v} className="cs-weekday">
                  <input
                    type="checkbox"
                    checked={selectedWeekdays.includes(d.v)}
                    onChange={() => toggleWeekday(d.v)}
                  />
                  {d.label}
                </label>
              ))}
            </div>
          </div>
          <div className="cs-row">
            <span className="cs-label" />
            <button type="button" className="cs-btn" disabled={busy} onClick={handleCloseWeekdays}>
              Fermer ces jours (chaque semaine)
            </button>
          </div>

          <div className="cs-row">
            <label className="cs-label">Date</label>
            <DateField className="cs-input cs-input-date" value={date} onChange={setDate} />
            <button type="button" className="cs-btn" disabled={busy} onClick={handleCloseDate}>
              Fermer cette date
            </button>
          </div>

          {error && <p className="cs-error">{error}</p>}
          {message && <p className="cs-message">{message}</p>}
        </div>
      </div>

      <div className="cs-card cs-table-card">
        <div className="cs-card-header">
          <span className="cs-card-title">Loteries fermees</span>
        </div>

        {loading && <p className="cs-loading">Chargement...</p>}

        {!loading && !error && byTirage.length === 0 && (
          <p className="cs-empty">Aucune loterie fermee.</p>
        )}

        {!loading && !error && byTirage.length > 0 && (
          <table className="cs-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Lottery</th>
                <th>End time</th>
                <th>Jours fermes</th>
                <th>Dates fermees</th>
              </tr>
            </thead>
            <tbody>
              {byTirage.map((row) => (
                <tr key={row.code}>
                  <td className="cs-td-code">{row.code}</td>
                  <td className="cs-td-name">{row.name}</td>
                  <td className="cs-td-time">{fmtEndTime(row.endTime)}</td>
                  <td>
                    {row.weekdays.length === 0 && <span className="cs-dash">—</span>}
                    {row.weekdays.map((w) => (
                      <span key={w.id} className="cs-chip">
                        {weekdayLabel(w.weekday)}
                        <span className="cs-chip-x" title="Rouvrir" onClick={() => handleReopen(w.id)}>×</span>
                      </span>
                    ))}
                  </td>
                  <td>
                    {row.dates.length === 0 && <span className="cs-dash">—</span>}
                    {row.dates.map((d) => (
                      <span key={d.id} className="cs-chip">
                        {d.date}
                        <span className="cs-chip-x" title="Rouvrir" onClick={() => handleReopen(d.id)}>×</span>
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default CloseSalesPage;
